import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Icon from "../../components/Icon/Icon";
import { IconColorE } from "../../models/enums";
import { TOAST_DURATION_LONG } from "../../utils/helpers/constants";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { checkIfNameIsValid } from "./helpers/checkIfNameIsValid";
import { getMaxAllowedCampaignNameChars } from "./helpers/getMaxAllowedCampaignNameChars";
import { WizardModeE } from "./models";

import styles from "./styles.module.scss";

type CustomNameExtraContentPropTypes = {
  defaultName: string;
  campaignName: string;
  tempName: string;
  mode: WizardModeE;
};

const CustomNameExtraContent: FC<CustomNameExtraContentPropTypes> = (props) => {
  const { campaignName, tempName, mode } = props;
  const [isNameEditMode, setIsNameEditMode] = useState(false);
  const maxChars = getMaxAllowedCampaignNameChars(
    campaignName,
    tempName,
    false
  );
  const { t } = useTranslate();

  const handleClickOutside = useCallback(
    (e: any) => {
      if (
        isNameEditMode &&
        e.target.tagName !== "INPUT" &&
        e.target.tagName !== "IMG" &&
        e.target.tagName !== "SPAN"
      ) {
        setIsNameEditMode(false);
      }
    },
    [isNameEditMode]
  );

  const handleKeyDown = (e) => {
    if (e.code === "Enter") {
      setIsNameEditMode(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isNameEditMode]);

  const handleApprove = () => {
    setIsNameEditMode(false);
  };

  const isCampaignNameFilled = useMemo(
    () =>
      !campaignName.includes("[Broadcasting date]") &&
      !campaignName.includes("[Advertiser name]") &&
      !campaignName.includes("[Brand name]"),
    [campaignName]
  );

  const handleStartEdit = useCallback(() => {
    if (!isCampaignNameFilled && mode === WizardModeE.Create) {
      toast.error(t`Fill in the fields above to edit the name`, {
        hideProgressBar: true,
        autoClose: TOAST_DURATION_LONG,
        position: toast.POSITION.TOP_CENTER,
        closeButton: false,
        style: {
          width: "340px",
        },
      });
    } else {
      const nameInput = document.getElementById("campaignName");
      if (nameInput) {
        nameInput.focus();
      }
      setIsNameEditMode(true);
    }
  }, [isCampaignNameFilled, mode, t]);

  return (
    <>
      {isNameEditMode && <span className={styles.maxChars}>{maxChars}</span>}
      {!isNameEditMode ? (
        <div
          className={styles.customNameHint}
          style={{ filter: !isCampaignNameFilled ? "grayscale(1)" : "none" }}
        >
          <span>
            {campaignName} —{" "}
            <span
              className={styles.customNameClickable}
              onClick={handleStartEdit}
            >
              {tempName || t`[Custom name]`}
            </span>
          </span>
          <Icon
            name="edit"
            title="Edit campaign name"
            onClick={handleStartEdit}
            color={IconColorE.DARK_GRAY}
            className={!isCampaignNameFilled ? styles.editCustomNameIcon : ""}
          />
        </div>
      ) : (
        <>
          <Icon
            name="approved"
            className={styles.approveCustomNameIcon}
            onClick={handleApprove}
          />
          <span className={styles.fullCampaignName}>
            {campaignName} — {tempName || t`[Custom name]`}
          </span>
        </>
      )}
    </>
  );
};

export default CustomNameExtraContent;
